import React, { useState } from 'react';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { translations } from './translations';
import './App.css';
import { FaFileUpload, FaEnvelope, FaCheckCircle, FaTimesCircle, FaSpinner} from 'react-icons/fa';
import { PT, FR, ES, CN, GB } from 'country-flag-icons/react/3x2';

function AppContent() {
  const { language, setLanguage } = useLanguage();
  const t = translations[language];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    description: '',
    file: null
  });

  const changeLanguage = (lng) => {
    setLanguage(lng);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); 
    
    try {
      let fileBase64 = null;
      if (formData.file) {
        if (formData.file.size > 5 * 1024 * 1024) {
          throw new Error(t.file_too_large);
        }
        fileBase64 = await readFileAsBase64(formData.file);
      }
  
      const bodyData = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: "'" + formData.phone,
        description: formData.description,
        ...(fileBase64 && { 
          file: fileBase64,
          fileName: formData.file.name,
          fileType: formData.file.type
        })
      };

      console.log('[REQUEST] Dados enviados:', JSON.stringify(bodyData, null, 2));
      console.log('[ENDPOINT] URL da API:', process.env.REACT_APP_API_URL);
  
      const response = await fetch(process.env.REACT_APP_API_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain'
        },
        body: JSON.stringify(bodyData),
      });
  
      console.log('[RESPONSE] Status:', response.status);
      const rawText = await response.text();
      console.log('[RESPONSE] Conteúdo:', rawText);

      let result;
  
      try {
        result = JSON.parse(rawText);
      } catch {
        throw new Error(t.default_error);
      }
  
      if (!response.ok || !result.success) {
        throw new Error(result.message || t.default_error);
      }
  
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        description: '',
        file: null
      });
  
      setIsSubmitted(true);
  
    } catch (error) {
      console.error('[ERROR] Erro no envio:', error);
      setErrorMessage(
        error.message === 'Failed to fetch' 
          ? t.network_error 
          : error.message
      );
    }
    finally {
      setIsSubmitting(false);
    }
  };
  

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <h1>{t.title}</h1>
          <h2>{t.subtitle}</h2>
          <div className="header-line"></div>
          <p className="header-subtitle">{t.years_experience}</p>
        </div>
      </header>

      <nav className="navbar">
        <div className="nav-links">
          <a href="#about" className="nav-link">{t.about_button}</a>
          <a href="#services" className="nav-link">{t.services_button}</a>
          <a href="#languages" className="nav-link">{t.languages_button}</a>
          <a href="#contact" className="nav-link">{t.contact_button}</a>
        </div>
        <button 
          className="quote-btn"
          onClick={() => {
            setIsSubmitted(false);
            setIsModalOpen(true);
          }}
        >
          {t.quote_button}
        </button>
      </nav>

      <main>
        <section id="about" className="section">
          <div className="section-content">
            <h3 className="section-title">{t.about_title}</h3>
            <p className="section-text">
              {t.about_text}
            </p>
          </div>
        </section>

        <section id="services" className="section">
          <div className="section-content">
            <h3 className="section-title">{t.services_title}</h3>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon">✍️</div>
                <h4>{t.services_text1}</h4>
              </div>
              <div className="service-card">
                <div className="service-icon">🔍</div>
                <h4>{t.services_text2}</h4>
              </div>
              <div className="service-card">
                <div className="service-icon">🌐</div>
                <h4>{t.services_text3}</h4>
              </div>
              <div className="service-card">
                <div className="service-icon">📜</div>
                <h4>{t.services_text4}</h4>
              </div>
            </div>
          </div>
        </section>

        <section id="languages" className="section">
          <div className="section-content">
            <h3 className="section-title">{t.languages_title}</h3>
            <div className="languages-grid">
              <div className="language-card">
                <div className="language-flag-container">
                  <CN className="language-flag" />
                  <GB className="language-flag" />
                </div>
                <h4>{t.language1}</h4>
              </div>
              <div className="language-card">
                <div className="language-flag-container">
                  <PT className="language-flag" />
                  <GB className="language-flag" />
                </div>
                <h4>{t.language2}</h4>
              </div>
              <div className="language-card">
                <div className="language-flag-container">
                  <ES className="language-flag" />
                  <GB className="language-flag" />
                </div>
                <h4>{t.language3}</h4>
              </div>
              <div className="language-card">
                <div className="language-flag-container">
                  <FR className="language-flag" />
                  <GB className="language-flag" />
                </div>
                <h4>{t.language4}</h4>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="section">
          <div className="section-content">
            <h3 className="section-title">{t.contact_title}</h3>
            <div className="contact-container">
              <div className="contact-info">
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <span>{t.email}</span>
                </div>
                <div className="contact-prose">
                  <p>{t.proz_text}</p>
                  <a href="https://www.proz.com/translator/1823924" className="proz_button" target='blank'>{t.proz_button}</a>
                </div>
              </div>
              <div className="contact-cta">
                <h4>{t.quote_title}</h4>
                <p>{t.quote_text}</p>
                <button 
                  className="quote-btn large"
                  onClick={() => {
                    setIsSubmitted(false);
                    setIsModalOpen(true);
                  }}
                >
                  {t.quote_button}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <p>© {new Date().getFullYear()} {t.footer_text}</p>
          <div className="language-selector">
            <button onClick={() => changeLanguage('en')}>English</button>
            <button onClick={() => changeLanguage('fr')}>Français</button>
            <button onClick={() => changeLanguage('pt')}>Português</button>
            <button onClick={() => changeLanguage('zh')}>简体中文</button>
            <button onClick={() => changeLanguage('zh_tw')}>繁體中文</button>
          </div>
        </div>
      </footer>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {!isSubmitted ? (
              <>
                <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                <h3 className="modal-title">{t.forms_title}</h3>
                <form onSubmit={handleSubmit} className="quote-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t.forms_name}</label>
                      <input 
                        type="text" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>{t.forms_enterprise}</label>
                      <input 
                        type="text" 
                        name="company" 
                        value={formData.company} 
                        onChange={handleInputChange} 
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t.forms_email}</label>
                      <input 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>{t.forms_phone}</label>
                      <input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleInputChange} 
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t.forms_description}</label>
                    <textarea 
                      name="description" 
                      value={formData.description} 
                      onChange={handleInputChange} 
                      required 
                      rows="4"
                    />
                  </div>

                  <div className="file-upload">
                    <label className="file-upload-label">
                      <FaFileUpload className="upload-icon" />
                      <span>{t.forms_file}</span>
                      <input 
                        type="file" 
                        onChange={handleFileChange} 
                        className="file-input"
                      />
                    </label>
                    {formData.file && (
                      <div className="file-selected">
                        {t.forms_selected_file} {formData.file.name}
                      </div>
                    )}
                  </div>

                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <div className="spinner-container">
                        <FaSpinner className="spinner-icon" />
                        {t.forms_submitting}
                      </div>
                    ) : (
                      t.forms_button
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="submission-success">
                <FaCheckCircle className="success-icon" />
                <h3>{t.modal_success_title}</h3>
                <p>{t.modal_success_message}</p>
                <button 
                  className="submit-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  {t.modal_close_button}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="modal-overlay" onClick={() => setErrorMessage(null)}>
          <div className="modal error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="submission-error">
              <FaTimesCircle className="error-icon" />
              <h3>{t.modal_error_title}</h3>
              <p>{errorMessage}</p>
              <button 
                className="submit-btn error-btn"
                onClick={() => setErrorMessage(null)}
              >
                {t.modal_close_button}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const App = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;